import React, { useEffect } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { clarityIdentify } from './utils/clarity';
import Toast from './components/Toast';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminPage from './pages/AdminPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { AuthProvider, useAuth } from './context/AuthContext';
import { InternalChatProvider } from './context/InternalChatContext';
import TeamChatWidget from './components/internal-chat/TeamChatWidget';
import SsoLogin from './pages/SsoLogin';
import QuoteCartPage from './pages/QuoteCartPage';
import QuoteViewPage from './pages/QuoteViewPage';
import './App.css';
import ViewToggle from './components/ViewToggle';
import ChatWidget from './components/ChatWidget';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SharedCartPage from './pages/SharedCartPage';
import MyCartsPage from './pages/MyCartsPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LocalCartPage from './pages/LocalCartPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import McpDocsPage from './pages/McpDocsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import GalleryPage from './pages/GalleryPage';
import StoreLayout from './storefront/StoreLayout';
import StoreHomePage from './storefront/StoreHomePage';
import StoreCatalogPage from './storefront/StoreCatalogPage';
import StoreProductPage from './storefront/StoreProductPage';
import StoreBagPage from './storefront/StoreBagPage';
import StoreWishlistPage from './storefront/StoreWishlistPage';
import StoreSearchPage from './storefront/StoreSearchPage';
import StoreCorporatePage from './storefront/StoreCorporatePage';
import StoreBrandsPage from './storefront/StoreBrandsPage';
/** Account, quote and staff pages: their own content inside the storefront's header and
 *  footer, so the whole site reads as one store. */
// Staff and account pages (dashboard, admin, quote and cart pages) in the store's header and
// footer. `portal` keeps the store's own page styles off them (see StoreLayout).
const LayoutWithHeader = () => (
  <StoreLayout wide portal>
    <div className="container">
      <Outlet />
    </div>
  </StoreLayout>
);

/** Old /products links (bookmarks, emails, other pages) land on the storefront listing. */
const ProductsRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  ['q', 'category', 'subcategory', 'brand'].forEach(k => params.get(k) && next.set(k, params.get(k)));
  return <Navigate to={`/catalog${next.toString() ? `?${next}` : ''}`} replace />;
};

// Storefront pages are mobile-first; the old "force desktop view" toggle would shrink them.
const STORE_PATHS = ['/', '/catalog', '/product/', '/bag', '/wishlist', '/search', '/brands', '/corporate'];
const isStorePath = (path) => STORE_PATHS.some(p => (p.endsWith('/') && p !== '/' ? path.startsWith(p) : path === p));

const UserIdentifier = () => {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.username) clarityIdentify(user.username);
  }, [user]);
  return null;
};

const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-state">Loading session...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const AUTH_PATHS = ['/login', '/register', '/verify-email', '/force-password-change',
                    '/sso-login', '/forgot-password', '/reset-password'];

function App() {
  const location = useLocation();
  const showChat = !AUTH_PATHS.some(p => location.pathname.startsWith(p))
    && !location.pathname.startsWith('/gallery');

  return (
    <ToastProvider>
    <ConfirmProvider>
    <InternalChatProvider>
      <UserIdentifier />
      <Toast />
      <Routes>
        {/* Storefront — the retail Brandstore's design on eYantra's catalogue */}
        <Route path="/" element={<StoreHomePage />} />
        <Route path="/catalog" element={<StoreCatalogPage />} />
        <Route path="/corporate" element={<StoreCorporatePage />} />
        <Route path="/brands" element={<StoreBrandsPage />} />
        <Route path="/product/:productId" element={<StoreProductPage />} />
        <Route path="/bag" element={<StoreBagPage />} />
        <Route path="/wishlist" element={<StoreWishlistPage />} />
        <Route path="/search" element={<StoreSearchPage />} />
        <Route path="/products" element={<ProductsRedirect />} />

        {/* Auth routes — no header */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/force-password-change" element={<ForcePasswordChange />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/sso-login" element={<SsoLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Public showcase gallery — no header, no login, shareable permalinks */}
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/gallery/:postId" element={<GalleryPage />} />

        {/* ALL Routes WITH the standard Header */}
        <Route element={<LayoutWithHeader />}>

          {/* Public routes */}
          <Route path="/quote" element={<QuoteViewPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/mcp" element={<McpDocsPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/cart" element={<LocalCartPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<HomePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/quote-cart" element={<QuoteCartPage />} />
            <Route path="/shared-cart" element={<SharedCartPage />} />
            <Route path="/my-carts" element={<MyCartsPage />} />
          </Route>

        </Route>
      </Routes>
      {!isStorePath(location.pathname) && <ViewToggle />}
      {showChat && <ChatWidget />}
      <TeamChatWidget />
    </InternalChatProvider>
    </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;